import { shallowRef } from 'vue'
import wan3ReferencePolicy from '@/views/video/model/wan3ReferencePolicy'
import h3VideoPolicy from '@/views/video/model/h3VideoPolicy'
import { referenceForDisplay, referenceDisplayUrl } from '@/views/video/model/referenceUrls'
import { TOKEN_KEY, USER_INFO_KEY } from '@/config/constants'
import { acceptVideoGeneration, submitAcceptedVideoGeneration, getVideoGenerationStatus,
  getVideoReferenceUploadConfig, pollVideoReferenceUpload, releaseVideoReferenceUpload,
  uploadVideoReferenceStream, updateVideoGenerationWorkflow, getVideoReferenceUploadResult } from '@/api/video'
import { uploadImageDirect, uploadFileDirect, compressImage } from '@/utils/fileUpload'
import { listSubmissions, putSubmission, readSubmissionFiles, putSubmissionFile,
  claimSubmissionWorker, saveSubmissionBatch, cleanSubmissionFiles } from './videoSubmissionStore'

export const VIDEO_OSS_QUEUE = 'video-oss-submit-queue'
export const VIDEO_MINIO_QUEUE = 'video-minio-submit-queue'
export const videoSubmissions = shallowRef([])
export const submissionLabels = {
  ACCEPTING: '正在提交', WAITING_UPLOAD: '等待上传', UPLOADING: '参考上传中',
  READY: '参考已就绪', SUBMITTING: '正在提交生成', UNCERTAIN: '正在确认提交结果',
  WAITING_SLOT: '等待生成名额',
  FAILED: '参考准备失败', SUBMITTED: '生成中', CANCELLED: '已取消'
}
const terminal = new Set(['SUBMITTED', 'CANCELLED', 'FAILED'])
// SUBMITTED is the browser worker's handoff state, not proof that upstream generation started.
const serverOwnsSubmission = (task, status) => status.workflowStatus === 'SUBMITTED'
  || (status.taskId && status.taskId !== task.id)
  || (task.request.platform === 'wan3-cd'
    && ['WAITING_SLOT', 'SUBMITTING', 'UNCERTAIN', 'CANCELLED', 'FAILED'].includes(status.workflowStatus))
const uuid = () => typeof crypto.randomUUID === 'function' ? crypto.randomUUID()
  : Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('')
const owner = `worker-${uuid()}`
const active = new Map()
let timer
let refreshing = false
let wakeQueue
let wakeScheduled = false

function session() {
  try {
    const user = JSON.parse(localStorage.getItem(USER_INFO_KEY) || 'null')
    const token = localStorage.getItem(TOKEN_KEY)
    return token && user ? { userId: String(user.userId || user.id), token } : null
  } catch (_) { return null }
}
function assertSession(expected, signal) {
  const current = session()
  if (signal.aborted || !current || current.token !== expected.token || current.userId !== expected.userId) {
    throw new DOMException('登录状态改变，任务已暂停', 'AbortError')
  }
}
function data(response) {
  if (response?.code !== 200 || response.data == null) throw new Error(response?.message || '请求失败')
  return response.data
}
function publicReference(item) {
  return referenceForDisplay(Object.fromEntries(['url', 'role', 'duration', 'volcAssetId', 'isVolcAsset', 'name', 'assetType', 'displayUrl']
    .filter(key => item[key] !== undefined).map(key => [key, item[key]])))
}
export function submissionReferenceSnapshot(task) {
  const refs = { images: [], videos: [], audios: [] }
  let inputReference = task.request.inputReference || ''
  for (const item of task.references) {
    if (item.single) inputReference = referenceDisplayUrl(item)
    else refs[`${item.category === 'image' ? 'image' : item.category === 'video' ? 'video' : 'audio'}s`].push(publicReference(item))
  }
  return { generationId: task.id, inputReference, referenceFiles: JSON.stringify(refs) }
}

export function referencePayload(task) {
  if (task.references.some(item => !item.url || item.url.startsWith('blob:'))) {
    throw new Error('参考文件尚未上传完成')
  }
  const snapshot = submissionReferenceSnapshot(task)
  const referenceError = h3VideoPolicy.getReferenceError(task.request.platform, JSON.parse(snapshot.referenceFiles))
  if (referenceError) throw new Error(referenceError)
  return snapshot
}

export async function refreshVideoSubmissions() {
  const current = session()
  const tasks = await listSubmissions()
  videoSubmissions.value = current ? tasks.filter(t => t.userId === current.userId) : []
  return tasks
}
async function save(task) {
  task.updatedAt = Date.now()
  await putSubmission(task)
  await refreshVideoSubmissions()
  if (wakeQueue) wakeQueue()
}

export async function enqueueVideoSubmissions(request, payload, count) {
  const referenceError = h3VideoPolicy.getReferenceError(request.platform, payload)
    || wan3ReferencePolicy.getWanReferenceError(request.platform, payload)
  if (referenceError) throw new Error(referenceError)
  const current = session()
  if (!current) throw new Error('请先登录')
  // Only local files need an upload route decision; URL-only tasks submit directly.
  let enabled = false
  const files = []
  const references = []
  const add = (item, category, single = false) => {
    const reference = { ...publicReference(item), category, single }
    if (item.file && !item.volcAssetId && !item.isVolcAsset) {
      reference.fileId = uuid()
      reference.url = ''
      files.push({ id: reference.fileId, userId: current.userId, file: item.file })
    }
    references.push(reference)
  }
  for (const category of ['image', 'video', 'audio']) (payload?.[`${category}s`] || []).forEach(item => add(item, category))
  if (payload?.inputFile) add({ file: payload.inputFile, name: payload.inputFile.name }, 'image', true)
  if (files.length) {
    enabled = data(await getVideoReferenceUploadConfig())
    if (typeof enabled !== 'boolean') throw new Error('上传配置无效，请重试')
    if (session()?.token !== current.token) throw new Error('登录状态已改变，请重新提交')
  }
  const snapshot = JSON.parse(JSON.stringify(request))
  if (snapshot.inputReference?.startsWith('blob:')) snapshot.inputReference = ''
  const tasks = Array.from({ length: count }, () => ({
    id: `gen-${uuid()}`, userId: current.userId, createdAt: Date.now(),
    queueName: enabled ? VIDEO_MINIO_QUEUE : VIDEO_OSS_QUEUE, request: snapshot,
    references: references.map(item => ({ ...item })), state: 'ACCEPTING', attempts: 0, nextAt: 0
  }))
  await saveSubmissionBatch(tasks, files)
  await refreshVideoSubmissions()
  startVideoSubmissionQueue()
  return tasks
}

async function step(task, current, signal) {
  const check = () => assertSession(current, signal)
  const options = { signal, expectedAuthToken: current.token, silent: true }
  check()
  if (task.state === 'ACCEPTING') {
    task.server = data(await acceptVideoGeneration({ ...task.request, generationId: task.id,
      uploadRoute: task.queueName === VIDEO_MINIO_QUEUE ? 'minio' : 'oss',
      images: undefined, videos: undefined, audios: undefined,
      referenceFiles: JSON.stringify({ images: [], videos: [], audios: [] }) }, options))
    if (serverOwnsSubmission(task, task.server)) {
      task.state = 'SUBMITTED'
      await save(task)
      await cleanSubmissionFiles()
      return
    }
    task.state = 'WAITING_UPLOAD'
    task.attempts = 0
    await save(task)
    if (task.references.some(item => item.fileId && !item.url)) return
  }
  if (task.state === 'UNCERTAIN' || task.state === 'SUBMITTING') {
    const status = data(await getVideoGenerationStatus(task.id, options))
    task.server = status
    if (serverOwnsSubmission(task, status)) {
      task.state = 'SUBMITTED'
      await save(task)
      await cleanSubmissionFiles()
      return
    }
    // A failed HTTP response is not proof that the provider did not accept a task.
    if (['ACCEPTED', 'WAITING_UPLOAD', 'UPLOADING', 'READY', 'UPLOAD_FAILED'].includes(status.workflowStatus)) {
      task.state = 'FAILED'
      task.resumeState = 'READY'
      task.error = task.error || '生成提交未受理，请检查参数后重试'
    }
    else { task.state = 'UNCERTAIN'; task.nextAt = Date.now() + 10000 }
    await save(task)
    return
  }
  const pending = task.references.find(item => item.fileId && !item.url)
  if (pending) {
    const cached = await readSubmissionFiles(pending.fileId)
    if (!cached || cached.userId !== current.userId) throw new Error('本地参考文件已丢失，请重新选择文件')
    if (cached.result) {
      Object.assign(pending, cached.result)
      await save(task)
      return
    }
    let file = cached.file
    if (pending.category === 'image') file = await compressImage(file)
    check()
    const queueId = `upload-${pending.fileId}`
    if (task.queueName === VIDEO_MINIO_QUEUE) {
      const receipt = data(await getVideoReferenceUploadResult(queueId, options))
      if (receipt.referenceUrl) {
        const result = { url: receipt.referenceUrl, displayUrl: receipt.displayUrl, objectName: receipt.objectName }
        await putSubmissionFile({ ...cached, result })
        Object.assign(pending, result)
        await save(task)
        return
      }
      const permit = data(await pollVideoReferenceUpload(queueId, options))
      if (!permit.granted) {
        task.state = 'WAITING_UPLOAD'
        task.position = permit.position
        task.nextAt = Date.now() + (permit.retryAfterMs || 2500) + Math.random() * 500
        await save(task)
        return
      }
    }
    task.state = 'UPLOADING'
    task.progress = 0
    await save(task)
    try {
      await updateVideoGenerationWorkflow(task.id, 'UPLOADING', options)
      check()
      let result
      if (task.queueName === VIDEO_MINIO_QUEUE) {
        const uploaded = data(await uploadVideoReferenceStream(file, pending.category, queueId, event => {
          task.progress = event.total ? Math.min(99, Math.round(event.loaded / event.total * 100)) : 0
          videoSubmissions.value = videoSubmissions.value.map(t => t.id === task.id ? { ...task } : t)
        }, options))
        if (!uploaded.referenceUrl || !uploaded.displayUrl) throw new Error('上传接口没有返回有效的参考地址')
        result = { url: uploaded.referenceUrl, displayUrl: uploaded.displayUrl, objectName: uploaded.objectName }
      } else {
        const url = pending.category === 'image'
          ? await uploadImageDirect(file, { ...options, compress: false })
          : await uploadFileDirect(file, options)
        result = { url, displayUrl: url }
      }
      // Persist the upload result before allowing any provider submission.
      await putSubmissionFile({ ...cached, result })
      Object.assign(pending, result)
      task.state = 'WAITING_UPLOAD'
      task.attempts = 0
      task.error = ''
      await save(task)
    } finally {
      if (task.queueName === VIDEO_MINIO_QUEUE && session()?.token === current.token) {
        await releaseVideoReferenceUpload(queueId).catch(() => {})
      }
    }
    return
  }
  check()
  const payload = referencePayload(task)
  task.state = 'SUBMITTING'
  await save(task)
  check()
  const submitted = data(await submitAcceptedVideoGeneration(payload, options))
  task.server = submitted
  task.state = 'SUBMITTED'
  task.error = ''
  await save(task)
  await cleanSubmissionFiles()
}

async function work(queueName, current) {
  if (active.has(queueName)) return
  if (navigator.locks) {
    // Native locks survive a throttled/background tab without lease takeover.
    return navigator.locks.request(`video-submit:${current.userId}:${queueName}`, { ifAvailable: true }, lock =>
      lock ? workWithLease(queueName, current) : undefined)
  }
  return workWithLease(queueName, current)
}

async function workWithLease(queueName, current) {
  if (active.has(queueName)) return
  const key = `${current.userId}:${queueName}`
  if (!await claimSubmissionWorker(key, owner)) return
  const controller = new AbortController()
  active.set(queueName, { controller, current })
  const renewal = setInterval(async () => {
    try {
      if (session()?.token !== current.token || !await claimSubmissionWorker(key, owner)) controller.abort()
    } catch (_) { controller.abort() }
  }, 5000)
  let task
  try {
    task = (await listSubmissions()).filter(t => t.userId === current.userId && t.queueName === queueName &&
      !terminal.has(t.state) && (t.nextAt || 0) <= Date.now()).sort((a, b) => a.createdAt - b.createdAt)[0]
    if (!task) return
    await step(task, current, controller.signal)
  } catch (error) {
    if (task && !controller.signal.aborted && error.name !== 'AbortError') {
      task.error = error.message || '网络异常，请重试'
      if (task.state === 'SUBMITTING' || task.state === 'UNCERTAIN') {
        task.state = 'UNCERTAIN'
        task.nextAt = Date.now() + 10000
      } else if (error.code === 503 && task.queueName === VIDEO_MINIO_QUEUE && task.server) {
        task.state = 'WAITING_UPLOAD'
        task.nextAt = Date.now() + 30000
      } else {
        task.attempts = (task.attempts || 0) + 1
        task.nextAt = Date.now() + Math.min(30000, 2000 * 2 ** task.attempts)
        task.resumeState = task.state === 'ACCEPTING' ? 'ACCEPTING' : 'WAITING_UPLOAD'
        task.state = task.attempts > 3 ? 'FAILED' : task.resumeState
      }
      await save(task)
      if (task.state === 'FAILED' && task.server && task.resumeState !== 'READY') {
        await updateVideoGenerationWorkflow(task.id, 'UPLOAD_FAILED', { expectedAuthToken: current.token, silent: true }).catch(() => {})
      }
    }
  } finally {
    clearInterval(renewal)
    active.delete(queueName)
    await claimSubmissionWorker(key, owner, true)
    const hasRunnable = (await listSubmissions()).some(item => item.userId === current.userId
      && item.queueName === queueName && !terminal.has(item.state) && (item.nextAt || 0) <= Date.now())
    if (hasRunnable && wakeQueue) wakeQueue()
  }
}

export async function retryVideoSubmission(id) {
  const current = session()
  const task = (await listSubmissions()).find(t => t.id === id && t.userId === current?.userId)
  if (!task || task.state !== 'FAILED') return
  task.state = task.resumeState || 'WAITING_UPLOAD'
  task.attempts = 0
  task.nextAt = 0
  task.error = ''
  await save(task)
}

export async function cancelVideoSubmission(id) {
  const current = session()
  const task = (await listSubmissions()).find(t => t.id === id && t.userId === current?.userId)
  if (!task || !['FAILED', 'WAITING_UPLOAD', 'ACCEPTING'].includes(task.state)) return
  const key = `${current.userId}:${task.queueName}`
  if (!await claimSubmissionWorker(key, `${owner}-cancel`)) throw new Error('任务正在处理，请稍后取消')
  try {
    // Idempotent accept resolves an earlier lost acknowledgement before cancellation.
    const options = { expectedAuthToken: current.token, silent: true }
    if (!task.server) task.server = data(await acceptVideoGeneration({ ...task.request, generationId: task.id }, options))
    await updateVideoGenerationWorkflow(task.id, 'CANCELLED', options)
    task.state = 'CANCELLED'
    await save(task)
    await cleanSubmissionFiles()
  } finally { await claimSubmissionWorker(key, `${owner}-cancel`, true) }
}

export function startVideoSubmissionQueue() {
  if (timer) return
  const tick = async () => {
    const current = session()
    for (const item of active.values()) if (!current || current.token !== item.current.token) item.controller.abort()
    if (refreshing) return
    refreshing = true
    try { await refreshVideoSubmissions() } catch (e) { console.warn('视频本地队列读取失败', e) } finally { refreshing = false }
    if (current) for (const queue of [VIDEO_OSS_QUEUE, VIDEO_MINIO_QUEUE]) work(queue, current).catch(e => console.warn('视频队列暂不可用', e))
  }
  wakeQueue = () => {
    if (wakeScheduled) return
    wakeScheduled = true
    setTimeout(() => {
      wakeScheduled = false
      tick()
    }, 0)
  }
  timer = setInterval(tick, 10000)
  tick()
}
